import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { StoreWithDeployStatus } from '@/models/operations/types'
import { ButtonApp, Container, Spinner } from '@/components'
import { useNotify } from '@/hooks'

import { BulkActionConfirmModal } from './components/BulkActionConfirmModal'
import { GroupBulkActionButtons } from './components/GroupBulkActionButtons'
import { GroupDetailContentSkeleton } from './components/OpsSkeletons'
import { StatCards } from './components/StatCards'
import { StoresTable } from './components/StoresTable'
import { useGroupDetailStores, useOperationsGroups } from './OperationsGroupsProvider'

export const OpsGroupDetail = () => {
  const navigate = useNavigate()
  const { notifyError } = useNotify()
  const {
    group,
    isLoading,
    groupId,
    loadError,
    stores,
    filters,
    setStatus,
    setSearch,
    activeCount,
    churnedCount,
    runStoreAction,
    isStoreActionRunning,
  } = useGroupDetailStores()

  const {
    startBulkAction,
    isBulkRunningForGroup,
    isDeployActionsLockedForGroup,
    getBulkRowActionStoreId,
    refreshGroup,
    isGroupRefreshing,
    isRefreshing: isGlobalRefresh,
  } = useOperationsGroups()

  const isRefreshingGroup = isGroupRefreshing(groupId)
  const isThisGroupBulkRunning = isBulkRunningForGroup(groupId)
  const isDeployLocked = isDeployActionsLockedForGroup(groupId, group?.summary.building ?? 0)

  const refreshDisabled = isRefreshingGroup || isGlobalRefresh
  const bulkActionsDisabled =
    isDeployLocked || isRefreshingGroup || isGlobalRefresh || (group?.summary.active ?? 0) === 0
  const tableInteractionsDisabled =
    isDeployLocked || isStoreActionRunning || isRefreshingGroup || isThisGroupBulkRunning
  const [pendingAction, setPendingAction] = useState<'redeploy' | 'clear-cache' | null>(null)
  const [isBulkConfirming, setIsBulkConfirming] = useState(false)
  const [clearCacheStoreId, setClearCacheStoreId] = useState<string | null>(null)

  const rowActionLoadingId = useMemo(() => {
    if (isStoreActionRunning && clearCacheStoreId) return clearCacheStoreId
    return getBulkRowActionStoreId(groupId)
  }, [clearCacheStoreId, getBulkRowActionStoreId, groupId, isStoreActionRunning])

  const handleBulkConfirm = () => {
    if (!group || !pendingAction) return

    const activeStores = group.stores.filter((store) => store.isActive)
    if (!activeStores.length) {
      notifyError('No active stores loaded for this group. Try checking status first.')
      setPendingAction(null)
      return
    }

    setIsBulkConfirming(true)
    startBulkAction(pendingAction, activeStores, group.id)
    setPendingAction(null)
    setIsBulkConfirming(false)
  }

  const handleRedeploy = (store: StoreWithDeployStatus) => {
    if (!store.isActive) return
    startBulkAction('redeploy', [store], groupId)
  }

  const handleClearCache = (store: StoreWithDeployStatus) => {
    setClearCacheStoreId(store.id)
    runStoreAction(
      { type: 'clear-cache', store, groupId },
      { onSettled: () => setClearCacheStoreId(null) },
    )
  }

  if (isLoading && !group && !loadError) {
    return <Spinner />
  }

  if (loadError) {
    return (
      <Container
        header={<p className="text-2xl font-bold">Group unavailable</p>}
        className="grow min-h-0"
        contentClassName="overflow-y-auto"
      >
        <div className="flex flex-col items-center justify-center gap-4 py-16 px-6 text-center max-w-lg mx-auto w-full">
          <ExclamationTriangleIcon className="w-10 h-10 text-red-200" />
          <div>
            <p className="text-sm text-gray-500">
              Stores for this group could not be loaded. Go back to the dashboard and use Check Status to
              try again.
            </p>
            <p className="mt-2 text-xs text-red-200 break-words">{loadError}</p>
          </div>
          <ButtonApp isPrimary handleClick={() => navigate('/operations/groups')}>
            Back to groups
          </ButtonApp>
        </div>
      </Container>
    )
  }

  if (!group) {
    return (
      <Container
        header={<p className="text-2xl font-bold">Group</p>}
        className="grow min-h-0"
        contentClassName="overflow-y-auto"
      >
        <div className="flex items-center justify-center py-16 text-gray-500 w-full">
          Group not found. Go back to the dashboard and check status.
        </div>
      </Container>
    )
  }

  return (
    <>
      <Container
        className="grow min-h-0"
        contentClassName="flex flex-col overflow-y-auto min-h-0 lg:overflow-hidden"
        header={
          <div className="flex xs:flex-col-reverse md:flex-row md:items-center md:justify-between gap-[15px]">
            <div className="min-w-0">
              <p className="text-2xl font-bold">{group.name}</p>
              {group.description && (
                <p className="text-sm text-gray-500 mt-1">{group.description}</p>
              )}
            </div>
            <GroupBulkActionButtons
              className="shrink-0"
              disabled={bulkActionsDisabled}
              refreshDisabled={refreshDisabled}
              isRefreshing={isRefreshingGroup}
              onRefresh={() => refreshGroup(groupId)}
              onRedeploy={() => setPendingAction('redeploy')}
              onClearCache={() => setPendingAction('clear-cache')}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-6 w-full min-h-0 flex-1 lg:h-full">
          {isRefreshingGroup ? (
            <GroupDetailContentSkeleton />
          ) : (
            <>
              <div className="shrink-0">
                <StatCards summary={group.summary} />
              </div>
              <StoresTable
                stores={stores}
                activeCount={activeCount}
                churnedCount={churnedCount}
                status={filters.status}
                search={filters.search}
                onStatusChange={setStatus}
                onSearchChange={setSearch}
                onRedeploy={handleRedeploy}
                onClearCache={handleClearCache}
                actionLoadingId={rowActionLoadingId}
                interactionsDisabled={tableInteractionsDisabled}
              />
            </>
          )}
        </div>
      </Container>

      <BulkActionConfirmModal
        visible={!!pendingAction}
        actionType={pendingAction}
        groupName={group.name}
        storeCount={group.summary.active}
        isLoading={isBulkConfirming}
        onConfirm={handleBulkConfirm}
        onHide={() => setPendingAction(null)}
      />
    </>
  )
}
